import { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider, Stack, Switch,
  TextField, Tooltip, Typography,
} from '@mui/material'
import { entitlementsApi, TenantEntitlements } from '../services/api'

/**
 * Platform-admin provisioning of per-tenant features.
 *
 * Two distinct states are shown per feature, and conflating them is the
 * mistake this component exists to avoid:
 *
 *   - the *grant* (the toggle)  — what is stored for this tenant
 *   - the *resolved* state      — whether it is actually active, after
 *                                 tier eligibility and the platform-wide
 *                                 kill switch are applied
 *
 * A feature can be granted but inactive (tier ineligible, or the env
 * kill switch is off in this environment). Showing only the resolved
 * state would make the toggle appear not to have saved.
 */
const TenantProvisioningPanel = ({
  tenantId,
  tenantTier,
}: {
  tenantId: string
  tenantTier?: string
}) => {
  const [data, setData] = useState<TenantEntitlements | null>(null)
  const [grants, setGrants] = useState<Record<string, boolean>>({})
  const [baseline, setBaseline] = useState<Record<string, boolean>>({})
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const load = useCallback(() => {
    if (!tenantId) return
    setLoading(true)
    entitlementsApi
      .get(tenantId)
      .then((resp) => {
        setData(resp.data)
        const g = resp.data.grants || {}
        setGrants({ ...g })
        setBaseline({ ...g })
        setError(null)
      })
      .catch(() => setError('Could not load entitlements for this tenant.'))
      .finally(() => setLoading(false))
  }, [tenantId])

  useEffect(load, [load])

  // Render order comes from the server's bundle map, falling back to the
  // known order. Not a list written down here: the mapping of feature to
  // bundle rides on each feature row for exactly that reason.
  const bundleOrder = Object.keys(data?.bundles || {})

  const changedKeys = Object.keys(grants).filter((k) => grants[k] !== baseline[k])
  const hasChanges = changedKeys.length > 0
  const eligible = data?.tier_eligible ?? false
  const enablingWhileIneligible =
    !eligible && changedKeys.some((k) => grants[k])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      // Send only what changed, so a concurrent change to another
      // feature is not silently reverted by this request.
      const delta: Record<string, boolean> = {}
      changedKeys.forEach((k) => {
        delta[k] = grants[k]
      })
      await entitlementsApi.set(tenantId, {
        features: delta,
        note: note.trim() || undefined,
      })
      setNote('')
      setSaved(true)
      load()
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 409) {
        setError(
          err?.response?.data?.detail ||
            'This tenant’s tier is not eligible for these features.',
        )
      } else if (status === 403) {
        setError('Only platform admins can change entitlements.')
      } else {
        setError(err?.response?.data?.detail || 'Could not save entitlements.')
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (!data?.grants) {
    return (
      <Alert severity="warning">
        {error || 'Entitlement provisioning is unavailable for this tenant.'}
      </Alert>
    )
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>Bundles are what a contract names</strong> — Core, Exposure,
        Evidence. The bundle switch sets every feature in it at once; the
        per-feature switches underneath are for repairing a partial grant.
        Tenant admins see this read-only and contact support to change it.
      </Typography>

      {!eligible && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Tier <strong>{data.tier || tenantTier || 'unknown'}</strong> is not
          eligible for these features — they are available on Enterprise only.
          Existing grants are preserved but stay inactive until the tenant is
          moved to Enterprise.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saved && !hasChanges && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>
          Entitlements updated.
        </Alert>
      )}

      <Divider sx={{ mb: 1 }} />

      {bundleOrder.map((name) => {
        const bundle = data.bundles?.[name]
        const members = data.features.filter((f) => f.bundle === name)
        if (members.length === 0) return null
        const grantedAll = members.every((f) => !!grants[f.key])
        const grantedSome = members.some((f) => !!grants[f.key])
        return (
          <Box key={name} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1">
                    {bundle?.label || name}
                  </Typography>
                  {bundle?.enabled && (
                    <Chip size="small" color="success" label="Active" />
                  )}
                  {/* The state this grouping exists to surface. A bundle
                      granted one key at a time with one key forgotten used to
                      look like a working bundle. */}
                  {bundle?.partial && (
                    <Tooltip title="Some of this bundle resolves and some does not — an incomplete grant, or a feature switched off in this environment. The customer bought the bundle, so this is not a state to leave it in.">
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label="Partially active"
                      />
                    </Tooltip>
                  )}
                  {bundle && !bundle.grantable && (
                    <Tooltip title="Included with any paid plan. There is no per-tenant switch, so a toggle here would appear to do something and do nothing.">
                      <Chip size="small" variant="outlined" label="Included" />
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {bundle?.description}
                </Typography>
              </Box>
              {/* The bundle is the switch. A customer buys Exposure, not four
                  of its five features — so this sets every member at once and
                  the per-feature switches below are for repairing a partial
                  grant. */}
              {bundle?.grantable !== false && (
                /* A Checkbox rather than a Switch, for `indeterminate`.
                   "Some of this bundle is granted" is a real state and the one
                   worth seeing at a glance; a two-position switch cannot show
                   it, and would read as off. */
                <Checkbox
                  checked={grantedAll}
                  indeterminate={grantedSome && !grantedAll}
                  disabled={saving}
                  onChange={(e) =>
                    setGrants((prev) => ({
                      ...prev,
                      ...Object.fromEntries(
                        members.map((f) => [f.key, e.target.checked]),
                      ),
                    }))
                  }
                />
              )}
            </Box>

            <Divider />
            <Stack divider={<Divider flexItem />} sx={{ pl: 2 }}>
              {members.map((feature) => {
                const granted = !!grants[feature.key]
                const suppressed = granted && !feature.enabled
                return (
                  <Box
                    key={feature.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      py: 1.25,
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{feature.label}</Typography>
                        {suppressed && (
                          <Tooltip title="Granted, but inactive — the tenant's tier is not eligible or the feature is switched off in this environment.">
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label="Granted · inactive"
                            />
                          </Tooltip>
                        )}
                        {granted && feature.enabled && (
                          <Chip size="small" color="success" label="Active" />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={granted}
                      disabled={saving || bundle?.grantable === false}
                      onChange={(e) =>
                        setGrants((prev) => ({
                          ...prev,
                          [feature.key]: e.target.checked,
                        }))
                      }
                    />
                  </Box>
                )
              })}
            </Stack>
          </Box>
        )
      })}

      {hasChanges && (
        <Box sx={{ mt: 2 }}>
          {enablingWhileIneligible && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Enabling a feature on an ineligible tier will be rejected. Move the
              tenant to Enterprise first.
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            label="Reason (support ticket or contract reference)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving
                ? 'Saving…'
                : `Save ${changedKeys.length} change${changedKeys.length === 1 ? '' : 's'}`}
            </Button>
            <Button
              onClick={() => {
                setGrants({ ...baseline })
                setNote('')
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default TenantProvisioningPanel
